import { Component, signal, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatStepperModule } from '@angular/material/stepper';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-user-direct-phone-change',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatTooltipModule,
    MatStepperModule,
  ],
  standalone: true,
  templateUrl: './user-direct-phone-change.component.html',
  styleUrl: './user-direct-phone-change.component.scss',
})
export class UserDirectPhoneChangeComponent implements OnDestroy {
  directPhoneForm!: FormGroup;
  userForm!: FormGroup;

  step = signal(0);

  token: Token | null = null;
  user: any = null;

  resendPhoneCodeTimerInitial = 30;
  resendPhoneCodeInterval: any = null;
  resendPhoneCodeTimer = signal(0);

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService,
    private router: Router
  ) {

    this.directPhoneForm = this.fb.group({
      direct_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    });

    this.userForm = this.fb.group({
      direct_phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
      token_code: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
      token_uuid: ['', Validators.required],
    });

    this.user = this.authService.getUserFromStorage();

    if (this.user) {
      this.directPhoneForm.patchValue({
        direct_phone: this.user.direct_phone,
      });
    }
  }

  createToken(): void {
    if (this.directPhoneForm.invalid) {
      this.appService.showSnackBar('Please enter a valid direct phone number', 'error');
      return;
    }

    if (!this.formChanged()) {
      this.appService.showSnackBar('No changes made', 'info');
      return;
    }

    const data = {
      identifier: this.directPhoneForm.value.direct_phone,
      unique_user_identifier: true,
      delivery_method: 'sms',
    };

    this.token = null;

    this.appService.post('create-token', data).subscribe({
      next: (response) => {
        this.token = response.data;

        this.step.set(1);

        this.startPhoneCodeResendTimer();

        this.userForm.patchValue({
          direct_phone: this.directPhoneForm.value.direct_phone,
          token_uuid: this.token?.uuid,
        });
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  updatePhone(): void {
    if (this.userForm.invalid) {
      this.appService.showSnackBar('Please enter a valid phone number and token', 'error');
      return;
    }

    this.authService.updateUserDirectPhone(this.userForm.value).subscribe({
      next: (response) => {
        this.appService.showSnackBar(response.message, 'success');
        this.authService.setUserToStorage(response.data);
        this.router.navigate(['/account']);
      },
      error: (error) => {
        this.appService.handleError(error);
      },
    });
  }

  startPhoneCodeResendTimer(): void {
    this.resendPhoneCodeTimer.set(this.resendPhoneCodeTimerInitial);

    this.resendPhoneCodeInterval = setInterval(() => {
      if (this.resendPhoneCodeTimer() > 0) {
        this.resendPhoneCodeTimer.set(this.resendPhoneCodeTimer() - 1);
      } else {
        this.clearPhoneCodeResendTimer();
      }
    }, 1000);
  }

  clearPhoneCodeResendTimer(): void {
    if (this.resendPhoneCodeInterval) {
      clearInterval(this.resendPhoneCodeInterval);
      this.resendPhoneCodeInterval = null;
    }
  }

  changePhone(): void {
    this.directPhoneForm.reset();

    if (this.user) {
      this.directPhoneForm.patchValue({
        direct_phone: this.user.direct_phone,
      });
    }

    this.userForm.reset();
    this.token = null;
    this.step.set(0);
    this.clearPhoneCodeResendTimer();
  }

  formChanged(): boolean {
    const direct_phone = this.directPhoneForm.get('direct_phone')?.value;

    if (this.user) {
      return direct_phone !== this.user.direct_phone;
    }

    return false;
  }

  goBack(): void {
    this.router.navigate(['/account']);
  }

  ngOnDestroy(): void {
    this.clearPhoneCodeResendTimer();
  }
}